import { Carousel } from "primereact/carousel";
import React, { useState, useEffect } from "react";
import {
  FaRocket,
  FaShieldAlt,
  FaUsers,
  FaCog,
  FaChartBar,
  FaCode,
  FaEye,
  FaArrowRight,
  FaCheck,
} from "react-icons/fa";
import cmsLogin from "../../assets/images/cms-login1.png";
import lmsLogin from "../../assets/images/lms-login1.png";
import { Button, Card, Container, IconTile, SectionHead } from "./ui";

const logins = [
  {
    title: "SMARTICA - CMS",
    subtitle: "Content Management System",
    description: `SMARTICA Content Management System (CMS) seamlessly facilitates users to easily acquire and manage diverse digital contents such as detailed course plans, structured lecture notes, high-quality video lectures, and other educational resources.`,
    highlights: [
      "User Roles and Permissions",
      "Workflow Management",
      "Template Management",
      "Media Management",
      "Responsive Design",
      "Integration Capabilities",
      "Creating webinar",
    ],
    image: cmsLogin,
    key: 1,
    icon: FaCog,
  },
  {
    title: "SMART - PRPP",
    subtitle: "Placement Readiness Platform or Portal",
    description: `SMART - PRPP (Placement Readiness Platform or Portal) is designed to creation, management, delivery and tracking of educational courses and training programs. It also enables students to access this learning platform and assessments.`,
    highlights: [
      "Course Creation and Customized Management",
      "Access to Resources Anytime, anywhere",
      "Face recognition during login",
      "Assessment and Feedback",
      "AI Monitored proctoring",
      "Data Analytics and Reporting",
      "Supports all coding languages",
    ],
    image: lmsLogin,
    key: 2,
    icon: FaRocket,
  },
];

const platformOverview = [
  {
    icon: FaCog,
    title: "Content Management",
    description:
      "Seamlessly manage and organize educational content with our advanced CMS platform.",
  },
  {
    icon: FaRocket,
    title: "Placement Readiness",
    description:
      "Deliver engaging learning experiences with our comprehensive SMART-PRPP platform.",
  },
  {
    icon: FaShieldAlt,
    title: "Secure & Reliable",
    description:
      "Enterprise-grade security and reliability for all your educational needs.",
  },
];

const platformFeatures = [
  {
    icon: FaUsers,
    title: "User Management",
    description: "Comprehensive user roles and permissions management",
  },
  {
    icon: FaChartBar,
    title: "Analytics",
    description: "Detailed analytics and reporting for better insights",
  },
  {
    icon: FaCode,
    title: "Multi-Language",
    description: "Support for all major programming languages",
  },
  {
    icon: FaEye,
    title: "AI Proctoring",
    description: "Advanced AI-powered monitoring and assessment",
  },
];

const SmarticaLogin = ({ loginsComponentRef }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setIsVisible(true),
      { threshold: 0.1 }
    );
    if (loginsComponentRef.current) {
      observer.observe(loginsComponentRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const navigateLogin = (type) => {
    const url =
      type === "CMS"
        ? "https://app.smartica.co.in/en/"
        : "https://learn.smartica.co.in/login";
    window.open(url, "_blank");
  };

  const renderLoginContent = (login) => (
    <Card
      key={login.title}
      className={`mx-2 overflow-hidden ${
        isVisible ? "animate-fade-in-up" : "opacity-0"
      }`}
    >
      <div className="grid lg:grid-cols-2">
        {/* Screenshot */}
        <div className="flex items-center justify-center bg-brand-50 p-8">
          <img
            src={login.image}
            alt={`${login.title} Interface`}
            className="h-auto w-full max-w-md rounded-2xl shadow-md"
          />
        </div>

        {/* Detail */}
        <div className="flex flex-col justify-center p-8 lg:p-10">
          <div className="mb-5 flex items-center gap-3">
            <IconTile icon={login.icon} size="lg" />
            <div>
              <h3 className="text-xl font-bold text-ink lg:text-2xl">
                {login.title}
              </h3>
              <p className="text-sm text-darkgrey">{login.subtitle}</p>
            </div>
          </div>

          <p className="text-sm leading-relaxed text-darkgrey">
            {login.description}
          </p>

          <h4 className="mb-4 mt-8 text-sm font-bold uppercase tracking-wider text-ink">
            Key Features
          </h4>
          <div className="grid gap-2.5 sm:grid-cols-2">
            {login.highlights.map((highlight) => (
              <div key={highlight} className="flex items-start gap-2">
                <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-brand-100">
                  <FaCheck className="h-2 w-2 text-brand" />
                </span>
                <span className="text-xs text-darkgrey">{highlight}</span>
              </div>
            ))}
          </div>

          <Button
            className="group mt-8"
            onClick={() => navigateLogin(login.title)}
          >
            Login to SMARTICA {login.title}
            <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
          </Button>
        </div>
      </div>
    </Card>
  );

  return (
    <section
      ref={loginsComponentRef}
      id="logins"
      className="scroll-mt-24 bg-white py-20"
    >
      <Container>
        <SectionHead
          eyebrow="Smart Learning Platform"
          title="SMARTICA platform"
          subtitle="Comprehensive learning and content management solutions for modern education."
        />

        {/* Overview */}
        <div className="mb-14 grid gap-6 md:grid-cols-3">
          {platformOverview.map((item) => (
            <Card key={item.title} className="flex items-start gap-4 p-5">
              <IconTile icon={item.icon} />
              <div>
                <h3 className="mb-1 text-base font-bold text-ink">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-darkgrey">
                  {item.description}
                </p>
              </div>
            </Card>
          ))}
        </div>

        <Carousel
          value={logins}
          numVisible={1}
          numScroll={1}
          itemTemplate={renderLoginContent}
          autoplayInterval={10000}
          circular={true}
          showIndicators={true}
          className="custom-carousel"
        />

        {/* Feature grid */}
        <div className="mt-16 rounded-3xl bg-brand-50 p-8 sm:p-12">
          <div className="mx-auto mb-10 max-w-2xl text-center">
            <h3 className="text-2xl font-bold text-ink sm:text-3xl">
              Why choose SMARTICA?
            </h3>
            <p className="mt-3 text-darkgrey">
              Advanced features designed to enhance the learning experience
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {platformFeatures.map((item) => (
              <Card key={item.title} className="p-5 text-center">
                <IconTile icon={item.icon} className="mx-auto mb-4" />
                <h4 className="mb-2 text-sm font-bold text-ink">
                  {item.title}
                </h4>
                <p className="text-xs leading-relaxed text-darkgrey">
                  {item.description}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
};

export default SmarticaLogin;
